(() => {
  if (customElements.get('tffc-cart-drawer')) return;
  class NativeCart extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();
      this.connection?.abort();
      this.connection = new AbortController();
      const listen = (target, name, handler) => target.addEventListener(name, handler, { signal: this.connection.signal });
      const note = this.querySelector('[data-cart-note]');
      this.savedNote = note?.dataset.saved || '';
      this.noteDraft = note?.value || '';
      this.renderVersion = 0;
      listen(this, 'click', event => this.onClick(event));
      listen(document, 'click', event => {
        const trigger = event.target.closest(`[aria-controls="${this.id}"]`);
        if (trigger) this.returnTarget = trigger;
      });
      listen(this, 'submit', event => this.onSubmit(event));
      listen(this, 'change', event => this.onServiceChange(event));
      this.querySelectorAll('[data-cart-service]').forEach(input => { input.disabled = false; });
      listen(this, 'input', event => {
        if (!event.target.matches('[data-cart-note]')) return;
        this.noteDraft = event.target.value;
        this.setNoteState(this.noteSave ? 'saving' : 'idle');
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => this.saveNote().catch(() => {}), 700);
      });
      for (const name of ['cart:updated', 'cart:refresh']) listen(document.documentElement, name, event => {
        if (event.detail?.openMiniCart) this.open = true;
        if (this.mutating) return;
        // Coalesce events for the same write into one section GET.
        this.queueRefresh();
      });
      listen(document.documentElement, 'cart:pending', () => {
        if (!this.querySelector('[data-cart-express]')) return;
        if (!window.TFFCCartState.busy && !this.mutating && !this.checkingOut && !this.noteSave) this.queueRefresh();
        this.syncExpressPayments();
      });
      listen(document, 'visibilitychange', () => { if (!document.hidden && this.open) this.updateDelivery(); });
      if (Shopify.designMode) listen(this, 'shopify:block:select', () => { this.open = true; });
      this.updateDelivery();
      this.syncExpressPayments();
    }

    disconnectedCallback() {
      this.connection?.abort();
      this.renderController?.abort();
      clearTimeout(this.refreshTimer);
      clearTimeout(this.noteTimer);
      clearTimeout(this.focusTimer);
      cancelAnimationFrame(this.giftScrollFrame);
      clearInterval(this.deliveryTimer);
      this.renderVersion++;
      this._focusTrap?.deactivate({ returnFocus: false });
      if (this.open) document.documentElement.classList.remove('lock-all');
      super.disconnectedCallback();
    }

    attributeChangedCallback(name, previous, value) {
      if (name !== 'open' || previous === value) return;
      clearTimeout(this.focusTimer);
      clearInterval(this.deliveryTimer);
      document.documentElement.classList.toggle('lock-all', this.open);
      if (this.open) {
        this.updateDelivery();
        this.focusTimer = setTimeout(() => { if (this.isConnected && this.open) this.focusTrap.activate(); }, 150);
        this.deliveryTimer = setInterval(() => this.updateDelivery(), 60000);
      } else {
        this._focusTrap?.deactivate();
        this.focusTimer = setTimeout(() => {
          if (this.isConnected && !this.open && this.returnTarget?.isConnected) this.returnTarget.focus({ preventScroll: true });
        }, 0);
      }
      this.dispatchEvent(new CustomEvent(`openable-element:${this.open ? 'open' : 'close'}`, { bubbles: true }));
    }

    updateDelivery() {
      if (!globalThis.TFFCDelivery) return;
      this.querySelectorAll('[data-cart-delivery]').forEach(row => {
        const dates = TFFCDelivery.window(new Date(), Number(row.dataset.minDays), Number(row.dataset.maxDays), Number(row.dataset.cutoff), (row.dataset.excluded || '').split(/[\s,]+/).filter(Boolean));
        row.querySelector('[data-delivery-dates]').textContent = TFFCDelivery.format(...dates);
      });
    }

    error(message = '') {
      const element = this.querySelector('[data-cart-error]');
      element.querySelector('[data-cart-error-message]').textContent = message;
      element.hidden = !message;
    }

    setBusy(value) {
      this.syncExpressPayments();
      this.setAttribute('aria-busy', String(value));
      this.querySelectorAll('button:not([data-action="close"]), [data-cart-service]').forEach(button => {
        if (value && !button.disabled) { button.dataset.cartBusy = ''; button.disabled = true; }
        else if (!value && button.hasAttribute('data-cart-busy')) { button.disabled = false; delete button.dataset.cartBusy; }
      });
    }

    syncExpressPayments() {
      const express = this.querySelector('[data-cart-express]');
      if (!express) return;
      const busy = Boolean(this.mutating || this.checkingOut || this.renderError || this.noteSave || this.noteError || this.noteDraft !== this.savedNote || window.TFFCCartState.busy);
      express.inert = busy;
      express.setAttribute('aria-busy', String(busy));
    }

    async write(action, body) {
      const service = action === 'add' && body.items?.find(item => item.parent_line_key && item.properties?._hand_tied_service === 'true');
      if (service) {
        const cart = await window.TFFCCartState.read({ fresh: true });
        let parent = cart.items.find(item => item.key === service.parent_line_key);
        if (!parent || parent.quantity < 1) throw new Error(this.dataset.error);
        const group = parent.properties?._hand_tied_group || crypto.randomUUID();
        if (!parent.properties?._hand_tied_group) {
          const changed = await this.write('change', { id: parent.key, quantity: parent.quantity, properties: { ...parent.properties, _hand_tied_group: group } });
          const candidates = changed.items?.filter(item => item.properties?._hand_tied_group === group && item.properties?._hand_tied_service !== 'true');
          if (candidates?.length !== 1) throw new Error(this.dataset.error);
          parent = candidates[0];
          if (this.mutationFocus) this.mutationFocus.key = parent.key;
        }
        service.parent_line_key = parent.key;
        service.quantity = parent.quantity;
        service.properties = { ...service.properties, _hand_tied_group: group };
      }
      const root = Shopify.routes?.root || '/';
      const response = await fetch(`${root}cart/${action}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
      });
      const result = await response.json();
      if (!response.ok) {
        const error = new Error(this.dataset.error);
        error.cartMessage = typeof result.description === 'string' ? result.description : this.dataset.error;
        throw error;
      }
      return result;
    }

    publish(cart) {
      if (!Array.isArray(cart?.items) || !Number.isInteger(cart.item_count)) throw new Error(this.dataset.error);
      document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    }

    setNoteState(state = this.noteState || 'idle') {
      this.noteState = state;
      this.syncExpressPayments();
      const button = this.querySelector('[data-save-note]');
      if (!button) return;
      const saving = state === 'saving';
      button.dataset.noteState = state;
      button.textContent = button.dataset[saving ? 'saving' : state === 'cleared' ? 'cleared' : state === 'saved' ? 'saved' : 'save'];
      button.toggleAttribute('data-cart-pending', saving);
      button.setAttribute('aria-busy', String(saving));
      if (saving) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
      const clear = this.querySelector('[data-clear-note]');
      clear.hidden = !this.noteDraft && !this.savedNote;
      clear.disabled = saving;
    }

    async saveNote(confirm = false) {
      clearTimeout(this.noteTimer);
      if (this.noteSave) { await this.noteSave; return this.saveNote(confirm); }
      if (this.noteDraft === this.savedNote && !this.noteError) {
        if (confirm) this.setNoteState(this.savedNote ? 'saved' : 'cleared');
        return;
      }
      const value = this.noteDraft;
      this.noteRevision = (this.noteRevision || 0) + 1;
      this.setNoteState('saving');
      this.noteSave = (async () => {
        try {
          const cart = await this.write('update', { note: value });
          if (cart.note !== value) throw new Error(this.dataset.noteError);
          this.savedNote = value;
          this.noteError = false;
          this.error();
          if (this.noteDraft === value) {
            this.setNoteState(value ? 'saved' : 'cleared');
            this.querySelector('[data-cart-status]').textContent = value ? this.dataset.noteSaved : this.dataset.noteCleared;
          }
        } catch (error) {
          this.noteError = true;
          this.setNoteState('idle');
          this.error(this.dataset.noteError);
          throw error;
        }
      })().finally(() => { this.noteSave = null; this.syncExpressPayments(); });
      await this.noteSave;
      if (this.noteDraft !== this.savedNote) return this.saveNote(confirm);
    }

    revealGiftMessage(details) {
      if (!this.open || !details.isConnected || !details.open || !matchMedia(window.themeVariables.breakpoints.pocket).matches) return;
      let scroll = this.querySelector('[data-cart-scroll]');
      if (getComputedStyle(scroll).overflowY === 'visible') scroll = this.querySelector('[data-cart-content]');
      const bounds = scroll.getBoundingClientRect();
      const section = details.getBoundingClientRect();
      const target = section.height <= scroll.clientHeight ? section : details.querySelector('[data-cart-note]').getBoundingClientRect();
      const top = bounds.top + scroll.clientTop;
      const bottom = top + scroll.clientHeight;
      if (target.bottom > bottom) scroll.scrollTop += target.bottom - bottom;
      else if (target.top < top) scroll.scrollTop += target.top - top;
    }

    onClick(event) {
      const giftToggle = event.target.closest('[data-cart-note-details] > summary');
      if (giftToggle) {
        cancelAnimationFrame(this.giftScrollFrame);
        this.giftScrollFrame = requestAnimationFrame(() => this.revealGiftMessage(giftToggle.parentElement));
        return;
      }
      const control = event.target.closest('[data-cart-change], [data-save-note], [data-clear-note], [data-cart-retry]');
      if (!control) return;
      event.preventDefault();
      if (this.mutating || this.checkingOut) return;
      if (control.hasAttribute('data-cart-retry')) {
        window.TFFCCartState.recoverAddedCart();
        return;
      }
      if (control.hasAttribute('data-clear-note')) {
        const restoreFocus = document.activeElement === control;
        this.noteDraft = '';
        const note = this.querySelector('[data-cart-note]');
        note.value = '';
        note.blur();
        this.saveNote(true).catch(() => {}).finally(() => {
          if (restoreFocus && this.open && !document.activeElement.matches('[data-cart-note]')) this.querySelector('[data-save-note]')?.focus({ preventScroll: true });
        });
        return;
      }
      if (control.hasAttribute('data-save-note')) {
        this.querySelector('[data-cart-note]')?.blur();
        if (!this.noteSave) this.saveNote(true).catch(() => {});
        return;
      }
      const item = control.closest('[data-cart-item]');
      this.mutate('change', { id: item.dataset.key, quantity: Number(control.dataset.cartChange) }, control);
    }

    async onServiceChange(event) {
      const input = event.target;
      if (!input.matches('[data-cart-service]')) return;
      const previous = input.defaultChecked;
      if (this.mutating || this.checkingOut) { input.checked = previous; return; }
      const data = input.dataset;
      const success = input.checked
        ? await this.mutate('add', { items: [{ id: Number(data.variantId), quantity: Number(data.quantity), parent_line_key: data.parentKey, properties: { _hand_tied_service: 'true' } }] }, input)
        : await this.mutate('change', { id: data.serviceKey, quantity: 0 }, input);
      if (!success && input.isConnected) input.checked = previous;
    }

    async onSubmit(event) {
      const form = event.target;
      if (!form.matches('[data-cart-add], [data-cart-checkout]')) return;
      event.preventDefault();
      if (this.mutating || this.checkingOut) return;
      if (form.matches('[data-cart-add]')) {
        const data = new FormData(form);
        const button = form.querySelector('button');
        const label = button.textContent;
        button.textContent = this.dataset.adding;
        button.setAttribute('aria-busy', 'true');
        try {
          const success = await this.mutate('add', { items: [{ id: Number(data.get('id')), quantity: Number(data.get('quantity')) }] });
          if (success && this.isConnected) {
            const status = this.querySelector('[data-cart-status]');
            status.textContent = this.dataset.added;
          }
        } finally {
          button.textContent = label;
          button.removeAttribute('aria-busy');
        }
        return;
      }
      this.checkingOut = true;
      this.setBusy(true);
      try {
        await this.saveNote();
        await window.TFFCCartState.settle({ repair: true });
        if (!this.isConnected) return;
        if (this.renderError) { location.assign(window.themeVariables.routes.cartUrl); return; }
        const checkout = document.createElement('input');
        checkout.type = 'hidden'; checkout.name = 'checkout'; checkout.value = 'Checkout';
        const currentForm = this.querySelector('[data-cart-checkout]');
        currentForm.appendChild(checkout);
        HTMLFormElement.prototype.submit.call(currentForm);
      } catch { this.error(this.noteError ? this.dataset.noteError : this.dataset.error); }
      finally { this.checkingOut = false; this.setBusy(false); }
    }

    async mutate(action, body, source) {
      this.mutating = true;
      const focused = source || document.activeElement;
      this.mutationFocus = { key: focused.closest('[data-cart-item]')?.dataset.key, action: focused.dataset.focus };
      const pending = source?.matches('[data-cart-service]')
        ? source.closest('.tffc-cart__hand-tying')
        : source?.matches('[data-cart-change]') ? source.closest('.tffc-cart__quantity') || source : null;
      if (pending) { pending.dataset.cartPending = ''; pending.setAttribute('aria-busy', 'true'); }
      const status = this.querySelector('[data-cart-status]');
      status.classList.add('visually-hidden');
      status.textContent = this.dataset.updating;
      this.setBusy(true);
      this.error();
      clearTimeout(this.refreshTimer);
      this.renderController?.abort();
      this.renderVersion++;
      try {
        await this.saveNote();
        const result = await this.write(action, body);
        const cart = action === 'add' ? await window.TFFCCartState.read({ fresh: true }) : result;
        this.publish(cart);
        await window.TFFCCartState.settle();
        await this.refresh();
        this.querySelector('[data-cart-status]').textContent = this.dataset.updated;
        this.dispatchEvent(new CustomEvent('cart:native:action', { bubbles: true, detail: { action, itemCount: cart.item_count } }));
        return true;
      } catch (error) {
        // The POST may have succeeded. Reconcile with a GET; never replay a write.
        try { this.publish(await window.TFFCCartState.read({ fresh: true })); await this.refresh(); } catch { /* Preserve the last usable basket. */ }
        this.error(this.noteError ? this.dataset.noteError : (error.cartMessage || this.dataset.error));
        return false;
      } finally {
        if (pending) { delete pending.dataset.cartPending; pending.removeAttribute('aria-busy'); }
        this.mutating = false;
        if (this.isConnected) {
          this.setBusy(false);
          // Restore only after enabling the replacement controls; disabling a focused input blurs it.
          if (this.open && this.mutationFocus?.key && !document.activeElement.matches('[data-cart-note]')) {
            const { key, action } = this.mutationFocus;
            const target = this.querySelector(`[data-key="${CSS.escape(key)}"] [data-focus="${CSS.escape(action || '')}"]`);
            (target && !target.disabled ? target : this.querySelector('[data-action="close"]')).focus({ preventScroll: true });
          }
        }
        this.mutationFocus = null;
      }
    }

    queueRefresh() {
      this.renderError = true;
      this.syncExpressPayments();
      clearTimeout(this.refreshTimer);
      const retry = this.querySelector('[data-cart-retry]');
      const retryFocused = document.activeElement === retry;
      retry.dataset.label ||= retry.textContent;
      retry.textContent = this.dataset.updating;
      retry.disabled = true;
      retry.setAttribute('aria-busy', 'true');
      this.refreshTimer = setTimeout(async () => {
        const refreshing = this.refresh(), version = this.renderVersion;
        try { await refreshing; }
        catch (error) {
          if (version === this.renderVersion && error.name !== 'AbortError') { this.renderError = true; this.error(this.dataset.error); }
        } finally {
          if (version === this.renderVersion) {
            retry.disabled = false;
            retry.textContent = retry.dataset.label;
            retry.removeAttribute('aria-busy');
            if (retryFocused && this.open) (this.renderError ? retry : this.querySelector('[data-action="close"]')).focus({ preventScroll: true });
          }
        }
      }, 30);
    }

    async refresh() {
      const version = ++this.renderVersion;
      this.renderError = true;
      this.syncExpressPayments();
      const noteRevision = this.noteRevision;
      this.renderController?.abort();
      const controller = this.renderController = new AbortController();
      await window.TFFCCartState.settle();
      if (!this.isConnected || version !== this.renderVersion) return;
      const url = new URL(window.themeVariables.routes.cartUrl, location.origin);
      url.searchParams.set('section_id', this.getAttribute('section'));
      const response = await fetch(url, { signal: controller.signal, cache: 'no-store' });
      if (!response.ok) { this.renderError = true; throw new Error(this.dataset.error); }
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('tffc-cart-drawer [data-cart-content]');
      if (!content || /Liquid (?:error|syntax error) \(/.test(html.body.textContent)) { this.renderError = true; throw new Error(this.dataset.error); }
      if (!this.isConnected || version !== this.renderVersion) return;
      this.renderError = false;
      if (!this.noteError) this.error();
      const focused = document.activeElement;
      const activeKey = focused.closest('[data-cart-item]')?.dataset.key;
      const focusAction = focused.dataset.focus;
      const noteFocused = focused.matches('[data-cart-note]');
      const selection = noteFocused ? [focused.selectionStart, focused.selectionEnd] : null;
      const noteOpen = this.querySelector('[data-cart-note-details]')?.open;
      const scroll = this.querySelector('[data-cart-scroll]')?.scrollTop || 0;
      this.querySelector('[data-cart-content]').replaceWith(content);
      window.TFFCReviewsStats?.updateTrustBar(this);
      this.querySelector('[data-cart-count]').textContent = html.querySelector('[data-cart-count]').textContent;
      this.querySelectorAll('[data-cart-service]').forEach(input => { input.disabled = false; });
      const note = this.querySelector('[data-cart-note]');
      if (note) {
        if (this.noteDraft !== this.savedNote || this.noteSave || noteRevision !== this.noteRevision) note.value = this.noteDraft;
        else { this.savedNote = note.dataset.saved; this.noteDraft = note.value; }
        if (noteOpen !== undefined) this.querySelector('[data-cart-note-details]').open = noteOpen;
        this.setNoteState();
      }
      this.querySelector('[data-cart-scroll]').scrollTop = scroll;
      if (this.open && !focused.isConnected && (!this.mutating || noteFocused)) {
        const target = noteFocused ? note : activeKey && this.querySelector(`[data-key="${CSS.escape(activeKey)}"] [data-focus="${CSS.escape(focusAction || '')}"]`);
        (target && !target.disabled ? target : this.querySelector('[data-action="close"]')).focus({ preventScroll: true });
        if (noteFocused && note && selection) note.setSelectionRange(...selection);
      }
      this.updateDelivery();
      this.syncExpressPayments();
      if (this.mutating || this.checkingOut) this.setBusy(true);
    }
  }
  customElements.define('tffc-cart-drawer', NativeCart);
})();
